import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import UnAuthedLayout from "../../component/UnAuthedLayout";
import PrimaryTextField from "../../component/PrimaryTextField";
import PrimaryButton from "../../component/PrimaryButton";
import PrimaryLink from "../../component/PrimaryLink";
import PrimarySelect from "../../component/PrimarySelect";
import {
  CustomLinkContainer,
  FieldContainer,
  ModalLink,
} from "./SingUp.styled";
import GetTeamSupportModal from "../../component/GetTeamSupportModal";
import { useDispatch } from "react-redux";
import { signupUser, updateUser } from "../../redux/thunk/userThunk";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { palette } from "../../themes/theme";

const referenceValues = [
  { title: "Social Media", value: "socialMedia" },
  { title: "Conference", value: "conference" },
  { title: "Referral", value: "referral" },
  { title: "Google", value: "google" },
  { title: "Others", value: "others" },
];

const SignUp = () => {
  const { action } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get user data from route state for edit mode
  const userToEdit = location.state?.user;
  const isEditMode = action === "edit";

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .max(50, "First Name must be at most 50 characters")
      .min(1)
      .required("First name is required")
      .trim("Empty name are not acceptable"),
    lastName: Yup.string()
      .max(50, "Last Name must be at most 50 characters")
      .min(1)
      .required("Last name is required")
      .trim("Empty name are not acceptable"),
    email: Yup.string()
      .email("Enter a valid email")
      .required("Email is required"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .test(
        "len",
        "Number must be exactly 10 digits",
        (val) => val === undefined || (val && val.length === 10)
      ),
    password: isEditMode
      ? Yup.string().test(
          "password-validation",
          "Password must meet the requirements",
          function (value) {
            // If empty in edit mode, it's valid (keeping existing password)
            if (!value || value === "") return true;

            // If provided, validate it
            const { createError } = this;

            if (value.length < 8 || value.length > 150) {
              return createError({
                message: "Password must be between 8 and 150 characters",
              });
            }
            if (!/[0-9]/.test(value)) {
              return createError({
                message: "Password must contain at least one number",
              });
            }
            if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
              return createError({
                message: "Password must contain at least one symbol",
              });
            }
            return true;
          }
        )
      : Yup.string()
          .required("Password is required")
          .matches(/[0-9]/, "Password must contain at least one number")
          .matches(
            /[!@#$%^&*(),.?":{}|<>]/,
            "Password must contain at least one symbol"
          )
          .min(8, "Password must be at least 8 characters long")
          .max(150, "Password must be at most 150 characters"),
    confirmPassword: isEditMode
      ? Yup.string().test(
          "passwords-match",
          "Passwords must match",
          function (value) {
            // Only validate if password field has a value
            const { password } = this.parent;
            if (!password || password === "") return true;
            return value === password;
          }
        )
      : Yup.string()
          .required("Please confirm your password")
          .oneOf([Yup.ref("password")], "Passwords must match"),
    selectedCountries: Yup.array()
      .min(1, "At least one country must be selected")
      .required("Country selection is required"),
    status: isEditMode
      ? Yup.boolean().required("Status is required")
      : Yup.boolean(),
    // .matches(/^\d+$/, "Phone Number must be numeric")
    // .required("Phone Number is required"),
    companyName: Yup.string()
      .max(200, "Name can be up to 200 characters")
      .trim("Empty name are not acceptable"),
    companyWebsite: Yup.string()
      .max(150, "The value can be up to 150 characters")
      .url("Enter a valid URL"),
  });

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      if (isEditMode) {
        // Update existing user
        const payload = {
          userId: userToEdit.id,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          companyWebsite: values.companyWebsite,
          companyName: values.companyName,
          phoneNumber: values.phoneNumber,
          roleId: userToEdit.roleId,
          selectedCountries: values.selectedCountries,
          status: values.status,
        };

        // Include endDate if provided (convert to ISO string)
        if (values.endDate) {
          payload.endDate =
            values.endDate instanceof Date
              ? values.endDate.toISOString()
              : new Date(values.endDate).toISOString();
        }

        // Only include password if it's provided
        if (values.password && values.password.trim() !== "") {
          payload.password = values.password;
        }

        await dispatch(updateUser(payload)).unwrap();
        navigate("/admin/user-management");
      } else {
        // Create new user
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          companyWebsite: values.companyWebsite,
          companyName: values.companyName,
          phoneNumber: values.phoneNumber,
          selectedCountries: values.selectedCountries,
          password: values.password,
        };
        // Include endDate if provided (convert to ISO string)
        if (values.endDate) {
          payload.endDate =
            values.endDate instanceof Date
              ? values.endDate.toISOString()
              : new Date(values.endDate).toISOString();
        }
        await dispatch(signupUser(payload)).unwrap();
        resetForm();
      }
    } catch (e) {
      console.log("error: ", e);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      endDate: null,
      // Default country should be USA selected
      selectedCountries: ["usa"],
      companyName: "",
      companyWebsite: "",
      referenceFrom: "",
      status: true, // Default to active
    },
    validationSchema,
    onSubmit: handleFormSubmit,
    enableReinitialize: true,
  });

  const { resetForm } = formik;

  // Populate form with user data in edit mode
  useEffect(() => {
    if (isEditMode && userToEdit) {
      formik.setValues({
        firstName: userToEdit.firstName || "",
        lastName: userToEdit.lastName || "",
        email: userToEdit.email || "",
        phoneNumber: userToEdit.phone?.toString() || "",
        password: "", // Don't set password in edit mode
        confirmPassword: "", // Don't set confirmPassword in edit mode
        endDate: userToEdit.endDate ? new Date(userToEdit.endDate) : null,
        // preserve selectedCountries if present on user, otherwise default to USA
        selectedCountries: userToEdit.selectedCountries || ["usa"],
        companyName: userToEdit.companyName || "",
        companyWebsite: userToEdit.website || "",
        referenceFrom: "",
        status: userToEdit.status !== undefined ? userToEdit.status : true,
      });
    }
  }, [isEditMode, userToEdit]);

  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <>
      <UnAuthedLayout showLoader={false} width="600px">
        <Typography
          variant="h5"
          gutterBottom
          color={palette.text.dark}
          fontWeight="bolder"
        >
          {action === "edit" ? "Edit" : "Add"} User
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <FieldContainer>
            <PrimaryTextField
              label="First Name"
              type="text"
              fullWidth
              margin="normal"
              variant="outlined"
              name="firstName"
              required
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
            <PrimaryTextField
              label="Last Name"
              type="text"
              fullWidth
              margin="normal"
              variant="outlined"
              name="lastName"
              required
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </FieldContainer>
          <FieldContainer>
            <PrimaryTextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              name="email"
              required
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <PrimaryTextField
              label="Phone Number"
              type="text"
              fullWidth
              margin="normal"
              variant="outlined"
              name="phoneNumber"
              required
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={
                formik.touched.phoneNumber && formik.errors.phoneNumber
              }
            />
          </FieldContainer>

          {/* Password Fields */}

          <FieldContainer>
            <PrimaryTextField
              label={isEditMode ? "Password (Optional)" : "Password"}
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              variant="outlined"
              name="password"
              required={!isEditMode}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: palette.text.dark }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PrimaryTextField
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              variant="outlined"
              name="confirmPassword"
              required={!isEditMode}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                      sx={{ color: palette.text.dark }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FieldContainer>

          <FieldContainer>
            <PrimaryTextField
              label="Company Name (Optional)"
              type="text"
              fullWidth
              margin="normal"
              variant="outlined"
              name="companyName"
              value={formik.values.companyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <PrimaryTextField
              label="Company Website (Optional)"
              type="text"
              fullWidth
              margin="normal"
              variant="outlined"
              name="companyWebsite"
              value={formik.values.companyWebsite}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.companyWebsite &&
                Boolean(formik.errors.companyWebsite)
              }
              helperText={
                formik.touched.companyWebsite && formik.errors.companyWebsite
              }
            />
          </FieldContainer>

          {/* End At date picker */}
          <FieldContainer>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                marginTop: "16px",
              }}
            >
              <DatePicker
                selected={formik.values.endDate}
                defaultValue={formik.values.endDate}
                onChange={(date) => {
                  formik.setFieldValue("endDate", date);
                }}
                onBlur={() => formik.setFieldTouched("endDate", true)}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select a end date"
                customInput={
                  <input
                    type="text"
                    style={{
                      width: "100%",
                      padding: "16px 22px",
                      border: "1px solid black",
                      borderRadius: "4px",
                      fontSize: "1rem",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                      backgroundColor: "white",
                      color: "black",
                      transition: "all 0.3s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = palette.primary.main;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "black";
                    }}
                  />
                }
              />
              {formik.touched.endDate && formik.errors.endDate && (
                <Typography
                  variant="caption"
                  sx={{ color: palette.text.danger, fontSize: "0.75rem" }}
                >
                  {formik.errors.endDate}
                </Typography>
              )}
            </Box>
          </FieldContainer>

          {/* Country Selection Checkboxes */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "flex-start",
              mt: 2,
              mb: 2,
            }}
          >
            <FormControl
              component="fieldset"
              margin="normal"
              required
              error={
                formik.touched.selectedCountries &&
                Boolean(formik.errors.selectedCountries)
              }
              sx={{ flex: 1, mt: 0 }}
            >
              <FormLabel
                component="legend"
                sx={{
                  color: palette.text.dark,
                  fontWeight: 600,
                  fontSize: "1rem",
                  mb: 1,
                  "&.Mui-focused": {
                    color: palette.text.dark,
                  },
                }}
              >
                Select Countries
              </FormLabel>
              <FormGroup row sx={{ gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.selectedCountries.includes("usa")}
                      onChange={(e) => {
                        const currentCountries =
                          formik.values.selectedCountries;
                        if (e.target.checked) {
                          formik.setFieldValue("selectedCountries", [
                            ...currentCountries,
                            "usa",
                          ]);
                        } else {
                          formik.setFieldValue(
                            "selectedCountries",
                            currentCountries.filter((c) => c !== "usa")
                          );
                        }
                      }}
                      onBlur={formik.handleBlur}
                      name="selectedCountries"
                      sx={{
                        color: palette.primary.main,
                        "&.Mui-checked": {
                          color: palette.primary.main,
                        },
                      }}
                    />
                  }
                  label="USA"
                  sx={{ color: palette.text.dark }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.selectedCountries.includes(
                        "canada"
                      )}
                      onChange={(e) => {
                        const currentCountries =
                          formik.values.selectedCountries;
                        if (e.target.checked) {
                          formik.setFieldValue("selectedCountries", [
                            ...currentCountries,
                            "canada",
                          ]);
                        } else {
                          formik.setFieldValue(
                            "selectedCountries",
                            currentCountries.filter((c) => c !== "canada")
                          );
                        }
                      }}
                      onBlur={formik.handleBlur}
                      name="selectedCountries"
                      sx={{
                        color: palette.primary.main,
                        "&.Mui-checked": {
                          color: palette.primary.main,
                        },
                      }}
                    />
                  }
                  label="Canada"
                  sx={{ color: palette.text.dark }}
                />
              </FormGroup>
              {formik.touched.selectedCountries &&
                formik.errors.selectedCountries && (
                  <FormHelperText sx={{ color: palette.text.danger }}>
                    {formik.errors.selectedCountries}
                  </FormHelperText>
                )}
            </FormControl>

            {/* Status Radio Buttons - Only in Edit Mode */}
            {isEditMode && (
              <FormControl
                component="fieldset"
                margin="normal"
                required
                error={formik.touched.status && Boolean(formik.errors.status)}
                sx={{ flex: 1, mt: 0 }}
              >
                <FormLabel
                  component="legend"
                  sx={{
                    color: palette.text.dark,
                    fontWeight: 600,
                    fontSize: "1rem",
                    mb: 1,
                    "&.Mui-focused": {
                      color: palette.text.dark,
                    },
                  }}
                >
                  Status
                </FormLabel>
                <RadioGroup
                  row
                  name="status"
                  value={formik.values.status}
                  onChange={(e) => {
                    formik.setFieldValue("status", e.target.value === "true");
                  }}
                  onBlur={formik.handleBlur}
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel
                    value={true}
                    control={
                      <Radio
                        sx={{
                          color: palette.primary.main,
                          "&.Mui-checked": {
                            color: palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Active"
                    sx={{ color: palette.text.dark }}
                  />
                  <FormControlLabel
                    value={false}
                    control={
                      <Radio
                        sx={{
                          color: palette.primary.main,
                          "&.Mui-checked": {
                            color: palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Inactive"
                    sx={{ color: palette.text.dark }}
                  />
                </RadioGroup>
                {formik.touched.status && formik.errors.status && (
                  <FormHelperText sx={{ color: palette.text.danger }}>
                    {formik.errors.status}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          </Box>

          {/* <PrimarySelect
            label="How did you hear about us?"
            onChange={formik.handleChange}
            selectedValue={formik.values.referenceFrom}
            values={referenceValues}
            name="referenceFrom"
          />
          {formik.touched.referenceFrom && formik.errors.referenceFrom && (
            <Typography color="error" variant="caption">
              {formik.errors.referenceFrom}
            </Typography>
          )}
          {formik.values.referenceFrom === "others" && (
            <PrimaryTextField
              label="Please specify"
              type="text"
              fullWidth
              margin="normal"
              variant="outlined"
              name="others"
              value={formik.values.others}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.others && Boolean(formik.errors.others)}
              helperText={formik.touched.others && formik.errors.others}
            />
          )} */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            sx={{ mt: "16px", fontWeight: "bold", color: palette.text.white }}
            disabled={loading}
          >
            {loading
              ? "Submitting..."
              : isEditMode
              ? "Update User"
              : "Add User"}
          </Button>
        </form>
        <Divider sx={{ my: "16px", backgroundColor: "#b7b7b7" }} />
        <CustomLinkContainer>
          <Typography variant="body2" color={palette.text.dark}>
            Go Back to{" "}
            <PrimaryLink to="/admin/user-management">
              User Management
            </PrimaryLink>
          </Typography>
        </CustomLinkContainer>
      </UnAuthedLayout>
      <GetTeamSupportModal
        handleModalClose={handleModalClose}
        isFrom="signup"
        showModal={showModal}
      />
    </>
  );
};

export default SignUp;
